<script setup lang="ts">
import type { SideBarMode } from '@/stores/sidebar'
import { useSidebarStore } from '@/stores/sidebar'
import AppHeaderMenuUserWithdraw from '@/components/App/Header/Menu/User/Withdraw/index.vue'
import AppHeaderSellerProfileBanner from '@/components/App/Header/Seller/Profile/Banner/index.vue'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

const emit = defineEmits<{
  close: []
}>()

defineOptions({
  name: 'UserMenuContent',
})

const user = useUser()
const userStripeAccount = useUserStripeAccount()
const featureFlags = useFeatureFlags()
const sidebar = useSidebarStore()

function logOut() {
  useFirebaseAuth().logOut()
  emit('close')
}

function handleClick(mode: SideBarMode) {
  sidebar.goTo({ mode })
  emit('close')
}

function handleNavigation() {
  emit('close')
}
</script>

<template>
  <div class="space-y-2">
    <!-- Stripe Account / Withdraw (if applicable) -->
    <div v-if="userStripeAccount && userStripeAccount?.state !== 'no account'" class="px-1">
      <AppHeaderSellerProfileBanner v-if="userStripeAccount.state === 'restricted'" />
      <AppHeaderMenuUserWithdraw v-else />
    </div>

    <Separator v-if="userStripeAccount && userStripeAccount?.state !== 'no account'" />

    <!-- Profile -->
    <Button
      v-if="user"
      variant="ghost"
      class="w-full justify-start h-12"
      as-child
      @click="handleNavigation"
    >
      <a :href="`/${user.username}`">
        <div class="i-material-symbols:account-circle w-4 h-4" />
        <span>Profile</span>
      </a>
    </Button>

    <!-- My Teams -->
    <Button
      v-if="user && featureFlags.teams?.enable"
      variant="ghost"
      class="w-full justify-start h-12"
      as-child
      @click="handleNavigation"
    >
      <a href="/my-teams">
        <div class="i-material-symbols:supervisor-account-outline-sharp w-4 h-4" />
        <span>My Teams</span>
      </a>
    </Button>

    <!-- API Keys -->
    <Button
      v-if="user && featureFlags.apiKeys?.enable"
      variant="ghost"
      class="w-full justify-start h-12"
      as-child
      @click="handleNavigation"
    >
      <a href="/api-keys">
        <div class="i-material-symbols:lock w-4 h-4" />
        <span>API Keys</span>
      </a>
    </Button>

    <!-- Settings -->
    <Button
      v-if="user"
      variant="ghost"
      class="w-full justify-start h-12"
      as-child
      @click="handleNavigation"
    >
      <a :href="`/${user.username}/edit`">
        <div class="i-tabler:settings transform rotate-90 w-4 h-4" />
        <span>Settings</span>
      </a>
    </Button>

    <!-- Knowledge Base -->
    <Button
      v-if="user"
      variant="ghost"
      class="w-full justify-start h-12"
      as-child
      @click="handleNavigation"
    >
      <a href="/library?filter_by=knowledge-base">
        <div class="i-lucide:book-open w-4 h-4" />
        <span>Knowledge Base</span>
      </a>
    </Button>

    <Button
      v-if="user"
      variant="ghost"
      class="w-full justify-start h-12"
      as-child
    >
      <a href="/workflows" @click="handleNavigation">
        <div class="i-tabler:git-merge transform rotate-90 w-4 h-4" />
        <span>Workflows</span>
      </a>
    </Button>

    <Button
      v-if="user"
      variant="ghost"
      class="w-full justify-start h-12"
      as-child
    >
      <a href="/webhooks" @click="handleNavigation">
        <div class="i-tabler:webhook transform rotate-90 w-4 h-4" />
        <span>Webhooks</span>
      </a>
    </Button>

    <Separator />

    <!-- Logout -->
    <Button
      variant="ghost"
      class="w-full justify-start h-12 text-destructive hover:text-destructive"
      @click="logOut"
    >
      <div class="i-material-symbols-logout-rounded w-4 h-4" />
      <span>Sign out</span>
    </Button>
  </div>
</template>
